import { useQuery } from '@tanstack/vue-query'
import reportBuilderService from "@/helpers/api/reportBuilder.service";
import { computed, ref, watch } from 'vue';

const LS_KEY = 'graspil_event_names_cache'

// Load persisted cache from localStorage on module init
function loadFromStorage() {
    try {
        const raw = localStorage.getItem(LS_KEY)
        return raw ? JSON.parse(raw) : {}
    } catch {
        return {}
    }
}

function saveToStorage(cache) {
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(cache))
    } catch { /* localStorage may be unavailable (private mode, quota exceeded) */ }
}

// Shared reactive cache — { event_id: { event_name } }
// Initialized from localStorage so names survive page reloads
const globalEventNamesCache = ref(loadFromStorage())

// Persist to localStorage whenever cache changes
watch(globalEventNamesCache, (val) => saveToStorage(val), { deep: true })

/**
 * Merge a flat { event_id: event_name } or { event_id: { event_name } } map into the cache.
 * Exported so callers that create an event on the fly (e.g. EventsSelector's inline
 * "create event" form) can show its name immediately, without waiting for a refetch.
 */
export function mergeIntoCache(namesMap) {
    if (!namesMap) return
    const patch = {}
    for (const [id, val] of Object.entries(namesMap)) {
        patch[id] = typeof val === 'object' ? val : { event_name: val }
    }
    globalEventNamesCache.value = { ...globalEventNamesCache.value, ...patch }
}

/**
 * Provides read-only access to the shared event names cache.
 * Triggers a lightweight background fetch of all event names on first use
 * when the cache is empty (e.g. first ever page load for this user).
 */
export function useEvents() {
    // Background fetch of all event names — fires only once (staleTime = 1h),
    // only when the user is authenticated (the request will 403 otherwise and
    // TanStack Query will not retry).
    const cacheIsEmpty = computed(() => Object.keys(globalEventNamesCache.value).length === 0)

    const { data: namesData } = useQuery({
        queryKey: ['eventNames'],
        queryFn: () => reportBuilderService.getEventNames(),
        staleTime: 1000 * 60 * 60, // 1 hour — event names rarely change
        enabled: cacheIsEmpty,     // skip if already populated from localStorage
        retry: false,
    })

    watch(namesData, (val) => {
        if (val) mergeIntoCache(val)
    }, { immediate: true })

    const eventsNameMap = computed(() => globalEventNamesCache.value)

    const getEventName = (eventId) => {
        const eventData = eventsNameMap.value[eventId?.event_id ?? eventId]
        return eventData?.event_name ?? `${eventId}`
    }

    const getEventNameComputed = (eventId) => computed(() => getEventName(eventId))

    return {
        isLoading: ref(false),
        isError: ref(false),
        error: ref(null),
        events: ref({}),
        resources: ref([]),
        eventsNameMap,
        getEventName,
        getEventNameComputed
    }
}

/**
 * Lazy per-bot events loader for EventsSelector.
 * Fires only when botKeyRef.value is set.
 * Populates globalEventNamesCache with each loaded bot's events.
 */
export function useBotEvents(botKeyRef) {
    const { data, isLoading, isError } = useQuery({
        queryKey: computed(() => ['botEvents', botKeyRef.value]),
        queryFn: () => reportBuilderService.getBotEvents({ bot_key: botKeyRef.value }),
        staleTime: 1000 * 60 * 5,
        enabled: computed(() => !!botKeyRef.value),
        retry: false,
    })

    watch(data, (newData) => {
        if (newData?.events_name) mergeIntoCache(newData.events_name)
    }, { immediate: true })

    const events = computed(() => data.value?.events || {})
    const eventsNameMap = computed(() => globalEventNamesCache.value)

    return { events, eventsNameMap, isLoading, isError }
}
